import React, { useState, useEffect } from 'react';
import useSortable from '../hooks/useSortable';
import SortableHeader from '../components/SortableHeader';
import { Plus, Search, Edit2, Trash2, X, User, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { UiSummaryCard, UiSummaryCards } from '../components/ui/UiSummaryCard';
import useAuthStore from '../store/authStore';
import VoiceInput from '../components/VoiceInput';
import { formatApiError } from '../utils/apiErrors';
import { formatDate } from '../utils/dateFormat';

const Users = ({ embedded = false }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '', email: '', first_name: '', last_name: '',
    phone: '', address: '', role: '', password: '', is_active: true
  });

  const { hasPermission, user: currentUser } = useAuthStore();

  useEffect(() => {
    fetchData();
  }, [roleFilter]);

  const fetchData = async () => {
    try {
      let url = '/users/?';
      if (roleFilter) url += `role=${roleFilter}`;
      
      const [usersRes, rolesRes] = await Promise.all([
        api.get(url),
        api.get('/roles/')
      ]);
      setUsers(usersRes.data.results || usersRes.data);
      setRoles(rolesRes.data.results || rolesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(formatApiError(error, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const { sortKey: uSortKey, sortDir: uSortDir, handleSort: uHandleSort, sortData: uSortData } = useSortable('username', 'asc');

  const filteredUsers = uSortData(users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.first_name + ' ' + user.last_name).toLowerCase().includes(searchQuery.toLowerCase())
  ));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.password) delete data.password;
      
      if (selectedUser) {
        await api.patch(`/users/${selectedUser.id}/`, data);
        toast.success('User updated successfully');
      } else {
        if (!data.password) {
          toast.error('Password is required for new users');
          return;
        }
        await api.post('/users/', data);
        toast.success('User created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save user'));
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      toast.error("You can't delete your own account");
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${id}/`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete user'));
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      address: user.address || '',
      role: user.role || '',
      password: '',
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedUser(null);
    setFormData({
      username: '', email: '', first_name: '', last_name: '',
      phone: '', address: '', role: '', password: '', is_active: true
    });
  };

  const getRoleColor = (roleName) => {
    switch (roleName) {
      case 'super_admin': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-blue-100 text-blue-700';
      case 'manager': return 'bg-green-100 text-green-700';
      case 'cashier': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {embedded ? (
        hasPermission('users', 'add') && (
          <div className="flex justify-end">
            <button onClick={() => { resetForm(); setShowModal(true); }} className="bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base">
              <Plus size={18} /> Add User
            </button>
          </div>
        )
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">User Management</h1>
            <p className="text-sm sm:text-base text-gray-500">Manage system users and their roles</p>
          </div>
          {hasPermission('users', 'add') && (
            <button onClick={() => { resetForm(); setShowModal(true); }} className="bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base">
              <Plus size={18} /> Add User
            </button>
          )}
        </div>
      )}

      {/* Stats */}
      <UiSummaryCards>
        <UiSummaryCard label="Total Users" value={users.length} />
        {roles.slice(0, 3).map((role) => (
          <UiSummaryCard key={role.id} label={`${role.name.replace('_', ' ')}s`} value={users.filter(u => u.role === role.id).length} className="capitalize" />
        ))}
      </UiSummaryCards>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input type="text" placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm" />
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="px-3 sm:px-4 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="">All Roles</option>
            {roles.map(role => (<option key={role.id} value={role.name}>{role.name.replace('_', ' ')}</option>))}
          </select>
        </div>
      </div>

      {/* Users Table - Desktop */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader label="User" col="username" sortKey={uSortKey} sortDir={uSortDir} onSort={uHandleSort} />
              <SortableHeader label="Email" col="email" sortKey={uSortKey} sortDir={uSortDir} onSort={uHandleSort} />
              <SortableHeader label="Role" col="role_name" sortKey={uSortKey} sortDir={uSortDir} onSort={uHandleSort} />
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Phone</th>
              <SortableHeader label="Status" col="is_active" sortKey={uSortKey} sortDir={uSortDir} onSort={uHandleSort} />
              <SortableHeader label="Joined" col="created_at" sortKey={uSortKey} sortDir={uSortDir} onSort={uHandleSort} />
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-medium">
                      {(user.first_name?.[0] || user.username[0]).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{user.first_name} {user.last_name}</p>
                      <p className="text-sm text-gray-500">@{user.username}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">{user.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full capitalize ${getRoleColor(user.role_name?.toLowerCase().replace(' ', '_'))}`}>
                    {user.role_name || 'No Role'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{user.phone || '-'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{formatDate(user.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {hasPermission('users', 'edit') && (
                      <button onClick={() => openEditModal(user)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Edit2 size={16} />
                      </button>
                    )}
                    {hasPermission('users', 'delete') && user.id !== currentUser?.id && (
                      <button onClick={() => handleDelete(user.id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500">No users found</div>
        )}
      </div>

      {/* Users Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {filteredUsers.map((user) => (
          <div key={user.id} className="bg-white rounded-xl shadow-sm p-4 border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-medium flex-shrink-0">
                {(user.first_name?.[0] || user.username[0]).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-800 truncate">{user.first_name} {user.last_name}</p>
                <p className="text-sm text-gray-500 truncate">@{user.username}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} flex-shrink-0`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <p className="text-xs text-gray-500">Role</p>
                <span className={`px-2 py-0.5 text-xs rounded-full capitalize inline-block ${getRoleColor(user.role_name?.toLowerCase().replace(' ', '_'))}`}>
                  {user.role_name || 'No Role'}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="font-medium">{user.phone || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium truncate">{user.email}</p>
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              {hasPermission('users', 'edit') && (
                <button onClick={() => openEditModal(user)} className="flex-1 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg font-medium flex items-center justify-center gap-1">
                  <Edit2 size={14} /> Edit
                </button>
              )}
              {hasPermission('users', 'delete') && user.id !== currentUser?.id && (
                <button onClick={() => handleDelete(user.id)} className="flex-1 py-2 text-sm text-red-600 bg-red-50 rounded-lg font-medium flex items-center justify-center gap-1">
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          </div>
        ))}
        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-xl">No users found</div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold">{selectedUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <VoiceInput type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="First name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <VoiceInput type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Last name" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                <VoiceInput type="text" required value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="Enter username" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <VoiceInput type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password {selectedUser ? '(leave blank to keep current)' : '*'}
                </label>
                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select required value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500">
                  <option value="">Select Role</option>
                  {roles.map(role => (<option key={role.id} value={role.id}>{role.name.replace('_', ' ')}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <VoiceInput type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="Phone number" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <VoiceInput isTextarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Enter address" rows={2} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4 text-primary-600 rounded" />
                <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
              </div>
              <div className="flex gap-2 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
